/**
 * tokenStore — the single place BigxBookWeb decides how auth tokens are held.
 *
 * ── Why this file exists ─────────────────────────────────────────────────────
 * Desktop keeps both tokens in Windows DPAPI via Electron `safeStorage`
 * (see BigxBookDesktop/src/services/httpClient.ts). A browser has no
 * equivalent: anything reachable from JavaScript is reachable by XSS.
 *
 * The backend returns `access_token` and a 30-day `refresh_token` in the JSON
 * body of `/auth/verify-otp` and `/auth/refresh`, and refresh rotates the pair.
 * A stolen refresh token is therefore worth 30 days of access.
 *
 * ── Current strategy (interim) ───────────────────────────────────────────────
 *   access token   — in memory only. Never written to any storage. Dies with
 *                    the page, and is re-minted from the refresh token.
 *   refresh token  — sessionStorage. Survives a reload (a till reloads; it
 *                    should not have to re-OTP), but not a tab close, and is
 *                    never shared with other tabs or persisted to disk.
 *
 * This is a real improvement over the naive `localStorage` approach — the
 * long-lived credential does not outlive the tab, and the access token is
 * never persisted at all — but it does NOT stop an XSS that runs while the
 * session is open.
 *
 * ── Target strategy (needs a small backend change) ───────────────────────────
 * The refresh token belongs in an `httpOnly; Secure; SameSite=Lax` cookie that
 * JavaScript cannot read. That requires `backend-api` to call `res.cookie(...)`
 * on verify-otp/refresh and to accept the cookie at /auth/refresh. It is
 * additive — the JSON body would be unchanged, so Desktop and Mobile are
 * unaffected — and cross-subdomain works because `pos.<domain>` and
 * `api.<domain>` are same-site.
 *
 * When that lands, flip `COOKIE_MODE` to true: `setTokens` stops storing the
 * refresh token and `getRefreshToken` returns null, so httpClient sends
 * `credentials: 'include'` and lets the cookie travel on its own. Nothing
 * outside this file changes.
 *
 * See BIGXBOOK_WEB_PLAN.md §7.2 and open item 1.
 */

/**
 * Set to true once backend-api issues the refresh token as an httpOnly cookie.
 * Keep in sync with the backend — a mismatch logs the user out on first refresh.
 */
export const COOKIE_MODE = false;

const REFRESH_KEY = 'auth.refreshToken';

/** Access token lives here and nowhere else. Cleared on reload by design. */
let accessToken: string | null = null;

/**
 * Listeners notified whenever the access token changes.
 *
 * `useAppStore.authToken` mirrors this value, and ported modules read the
 * mirror — `sync/index.ts` uses it both as the "am I authenticated" check and
 * as the Bearer token. If the two drift, sync returns early and the UI shows a
 * frozen last-sync time with no error, which is what happened on web: the
 * access token is memory-only, so after a reload the mirror was null while
 * httpClient silently refreshed its own copy here.
 *
 * Kept as a plain listener list rather than importing the store, so this module
 * stays free of app-level imports and there is no cycle.
 */
type TokenListener = (token: string | null) => void;
const listeners = new Set<TokenListener>();

export function onAccessTokenChange(fn: TokenListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(token: string | null): void {
  for (const fn of listeners) {
    try {
      fn(token);
    } catch {
      /* a bad listener must not break auth */
    }
  }
}

export const tokenStore = {
  /** In-memory only — deliberately never persisted. */
  getAccessToken(): string | null {
    return accessToken;
  },

  setAccessToken(token: string | null): void {
    accessToken = token;
    notify(token);
  },

  getRefreshToken(): string | null {
    // In cookie mode the browser holds it; JS neither can nor should read it.
    if (COOKIE_MODE) return null;
    try {
      return sessionStorage.getItem(REFRESH_KEY);
    } catch {
      // Private mode or blocked site data — treat as no session.
      return null;
    }
  },

  setTokens(access: string | null, refresh: string | null): void {
    accessToken = access;
    notify(access);
    if (COOKIE_MODE) return;
    try {
      if (refresh) sessionStorage.setItem(REFRESH_KEY, refresh);
    } catch {
      /* storage blocked — session degrades to this page load only */
    }
  },

  clear(): void {
    accessToken = null;
    notify(null);
    try {
      sessionStorage.removeItem(REFRESH_KEY);
    } catch {
      /* ignore */
    }
  },

  /**
   * True when a session can plausibly be resumed. An access token in memory is
   * a live session; a refresh token alone means the page reloaded and the
   * access token must be re-minted before use.
   */
  hasSession(): boolean {
    return Boolean(accessToken) || Boolean(tokenStore.getRefreshToken());
  },

  /** True when the access token is gone but the session can be recovered. */
  needsRefresh(): boolean {
    return !accessToken && (COOKIE_MODE || Boolean(tokenStore.getRefreshToken()));
  },
};
